import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Sparkles, MapPin, Video, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const OFFLINE = ['workplace_visit', 'offline', 'in-person', 'in_person'];
export const isOfflineMode = (m) => OFFLINE.includes(String(m || '').toLowerCase());

export const formatDay = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
};

export const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${period}`;
};

export const venueText = (v) =>
  v ? [v.workplace_name, v.address, v.city].filter(Boolean).join(', ') : '';

export default function AssignMentorDialog({ booking, onClose, onAssigned, mode = 'assign' }) {
  const rescheduling = mode === 'reschedule';
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mentorId, setMentorId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');

  const offline = isOfflineMode(booking?.requested_mode);

  useEffect(() => {
    if (!booking) return;
    let active = true;
    setLoading(true); setError(''); setMentors([]);
    setMentorId(''); setDate(''); setTime(''); setNotes('');
    api.get(`/bookings/${booking.id}/suggested-mentors`)
      .then((r) => {
        if (!active) return;
        const list = r.data || [];
        setMentors(list);
        if (mode === 'reschedule') {
          // Start from the time CareersPOV found, on the current mentor where possible
          const wanted = booking.reschedule_proposal?.assigned_date;
          const current = list.find((m) => m.id === booking.mentor_profile_id && m.assignable);
          const candidates = current ? [current, ...list.filter((m) => m !== current && m.assignable)]
            : list.filter((m) => m.assignable);
          for (const m of candidates) {
            const slots = offline ? m.offline_slots : m.available_slots;
            const hit = wanted ? slots.find((sl) => sl.date === wanted) : slots[0];
            if (hit) { setMentorId(m.id); setDate(hit.date); setTime(hit.time); return; }
          }
          return;
        }
        // Pre-select CareersPOV's suggestion when it is still valid
        const suggested = list.find((m) => m.id === booking.suggested_mentor_id);
        if (suggested && suggested.assignable) {
          const slots = offline ? suggested.offline_slots : suggested.available_slots;
          const match = slots.find(
            (s) => s.date === booking.suggested_date && s.time === booking.suggested_time_slot,
          );
          setMentorId(suggested.id);
          if (match) { setDate(match.date); setTime(match.time); }
        }
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.detail || 'Could not load eligible mentors.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [booking, offline]);

  const selected = mentors.find((m) => m.id === mentorId);
  const slots = useMemo(
    () => (selected ? (offline ? selected.offline_slots : selected.available_slots) : []),
    [selected, offline],
  );
  const dates = useMemo(() => [...new Set(slots.map((s) => s.date))], [slots]);
  const times = useMemo(() => slots.filter((s) => s.date === date), [slots, date]);
  const venue = times.find((s) => s.time === time)?.venue || times[0]?.venue;

  const pickMentor = (m) => {
    if (!m.assignable) return;
    setMentorId(m.id); setError('');
    const mSlots = offline ? m.offline_slots : m.available_slots;
    const first = mSlots[0];
    setDate(first?.date || ''); setTime(first?.time || '');
  };

  const useSuggestion = () => {
    const m = mentors.find((x) => x.id === booking.suggested_mentor_id);
    if (!m) return;
    setMentorId(m.id);
    setDate(booking.suggested_date || '');
    setTime(booking.suggested_time_slot || '');
    setError('');
  };

  const submit = async () => {
    if (!mentorId || !date || !time) {
      setError('Select a mentor, date and time slot before assigning.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const res = await api.put(
        `/coordinator/bookings/${booking.id}/${rescheduling ? 'reschedule' : 'assign'}`,
        {
          mentor_profile_id: mentorId,
          assigned_date: date,
          assigned_time_slot: time,
          coordinator_notes: notes.trim() || null,
        },
      );
      toast.success(
        `${rescheduling ? 'Moved to' : 'Assigned to'} ${selected?.parent_name || 'mentor'} on ${formatDay(date)} at ${formatTime(time)} — awaiting parent confirmation`,
      );
      if (!offline && res.data?.google_sync_status && res.data.google_sync_status !== 'synced') {
        toast.warning('The calendar invitation has not gone out yet. The assignment is saved — an admin can retry it from Integrations.', { duration: 8000 });
      }
      onAssigned(res.data);
    } catch (err) {
      setError(err.response?.data?.detail
        || `${rescheduling ? 'Reschedule' : 'Assignment'} failed — the original booking is unchanged.`);
    }
    setSubmitting(false);
  };

  const suggestionAvailable = !rescheduling && booking?.suggested_mentor_id
    && mentors.some((m) => m.id === booking.suggested_mentor_id);
  const suggestedMentor = mentors.find((m) => m.id === booking?.suggested_mentor_id);
  const suggestionSlotValid = !!suggestedMentor && (offline ? suggestedMentor.offline_slots : suggestedMentor.available_slots)
    .some((s) => s.date === booking.suggested_date && s.time === booking.suggested_time_slot);
  const suggestionApplied = suggestionAvailable
    && mentorId === booking.suggested_mentor_id
    && date === booking.suggested_date
    && time === booking.suggested_time_slot;

  return (
    <Dialog open={!!booking} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{rescheduling ? 'Reschedule Experience' : 'Assign Mentor'}</DialogTitle>
          <DialogDescription>
            {booking?.career_name} · {booking?.child_name}
            {booking?.child_grade ? ` (Grade ${booking.child_grade})` : ''}
          </DialogDescription>
        </DialogHeader>

        {rescheduling && (
          <div className="rounded-xl bg-[#F8F9FA] px-3 py-2.5 text-sm text-[#4B5563]" data-testid="reschedule-context">
            <p>
              {['awaiting_reschedule', 'waitlisted'].includes(booking?.status) ? 'Was ' : 'Currently '}
              {booking?.assigned_date ? formatDay(booking.assigned_date) : 'unscheduled'}
              {booking?.assigned_time_slot ? ` at ${formatTime(booking.assigned_time_slot)}` : ''}
              {booking?.mentor_name ? ` with ${booking.mentor_name}` : ''}.
              {['awaiting_reschedule', 'waitlisted'].includes(booking?.status)
                ? ' That slot is already released — the experience is active and waiting for a new one.'
                : ' The current booking stays in place until the new slot is saved.'}
            </p>
            {booking?.reschedule_request?.status === 'pending' && (
              <p className="mt-1 text-[#F06651]" data-testid="reschedule-preference">
                Parent's reason: "{booking.reschedule_request.reason}"
                {booking.reschedule_proposal
                  ? ` · found ${formatDay(booking.reschedule_proposal.assigned_date)} at ${formatTime(booking.reschedule_proposal.assigned_time_slot)}`
                  : ' · no time found automatically'}
              </p>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Badge
            className={`border-0 text-[11px] ${offline ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}
            data-testid="assign-mode-badge"
          >
            {offline
              ? <><MapPin className="h-3 w-3 mr-1 inline" />Workplace Visit</>
              : <><Video className="h-3 w-3 mr-1 inline" />Online session</>}
          </Badge>
          <span className="text-xs text-[#9CA3AF]">
            {offline
              ? 'Slots come from the mentor\'s published workplace visits'
              : 'Slots come from the mentor\'s weekly availability'}
          </span>
        </div>

        {booking?.parent_notes && (
          <p className="text-sm text-[#6B7280] bg-[#F8F9FA] rounded-lg px-3 py-2" data-testid="assign-parent-notes">
            <span className="text-[#9CA3AF]">Parent notes: </span>{booking.parent_notes}
          </p>
        )}

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div>
        ) : (
          <div className="space-y-4">
            {suggestionAvailable && (
              <div className="rounded-xl border border-[#FBD5CE] bg-[#FFF7F5] px-3 py-2.5" data-testid="assign-suggestion">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#F06651] flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> CareersPOV suggestion
                    </p>
                    <p className="text-sm text-[#1F2937] mt-1">
                      {booking.suggested_mentor_name}
                      {booking.suggested_date ? ` · ${formatDay(booking.suggested_date)}` : ''}
                      {booking.suggested_time_slot ? ` at ${formatTime(booking.suggested_time_slot)}` : ''}
                    </p>
                    {!suggestionSlotValid && (
                      <p className="text-xs text-[#9CA3AF] mt-1" data-testid="assign-suggestion-stale">
                        {offline
                          ? 'This slot is from the mentor\'s weekly availability, not a workplace visit — pick a visit slot below.'
                          : 'That slot is no longer open — pick another slot below.'}
                      </p>
                    )}
                  </div>
                  {suggestionApplied ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1 shrink-0" data-testid="assign-suggestion-applied">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Selected
                    </span>
                  ) : suggestionSlotValid ? (
                    <Button
                      size="sm" variant="outline" onClick={useSuggestion}
                      className="rounded-lg text-xs border-[#FBD5CE] text-[#F06651] shrink-0"
                      data-testid="assign-use-suggestion-btn"
                    >
                      Use suggestion
                    </Button>
                  ) : null}
                </div>
              </div>
            )}

            <div>
              <Label className="text-sm text-[#6B7280]">
                Eligible mentors for {booking?.career_name} ({mentors.length})
              </Label>
              {mentors.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] mt-2 bg-[#F8F9FA] rounded-lg px-3 py-3" data-testid="assign-no-mentors">
                  No approved mentor at this school is mapped to this career yet. The request stays unfulfilled until one is approved.
                </p>
              ) : (
                <div className="mt-1.5 space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {mentors.map((m) => {
                    const active = m.id === mentorId;
                    return (
                      <button
                        key={m.id} type="button" onClick={() => pickMentor(m)} disabled={!m.assignable}
                        className={`w-full text-left rounded-xl border px-3 py-2 transition-colors ${
                          active ? 'border-[#F97D6A] bg-[#FFF7F5]'
                            : m.assignable ? 'border-[#E5E7EB] hover:border-[#F97D6A] bg-white'
                              : 'border-[#F3F4F6] bg-[#FAFAFA] opacity-60 cursor-not-allowed'
                        }`}
                        data-testid={`assign-mentor-option-${m.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-[#1F2937] truncate">{m.parent_name}</p>
                          <div className="flex items-center gap-1 shrink-0">
                            {m.is_suggested && <Badge className="border-0 bg-[#FFF0ED] text-[#F06651] text-[9px]">Suggested</Badge>}
                            {!offline && m.mode_match && <Badge className="border-0 bg-blue-50 text-blue-700 text-[9px]">Mode match</Badge>}
                          </div>
                        </div>
                        <p className="text-sm text-[#6B7280] truncate">
                          {m.profession_title}{m.company_name ? ` · ${m.company_name}` : ''}
                        </p>
                        <p className="text-[11px] text-[#9CA3AF] mt-0.5">
                          {m.assignable
                            ? `${(offline ? m.offline_slots : m.available_slots).length} slot(s) available · ${m.assign_count} experience(s) so far`
                            : m.unassignable_reason}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {selected && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-sm text-[#6B7280]">Date</Label>
                  <Select value={date} onValueChange={(v) => { setDate(v); setTime(''); }}>
                    <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="assign-date-select">
                      <SelectValue placeholder="Select date" />
                    </SelectTrigger>
                    <SelectContent>
                      {dates.map((d) => <SelectItem key={d} value={d}>{formatDay(d)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-[#6B7280]">Time slot</Label>
                  <Select value={time} onValueChange={setTime} disabled={!date}>
                    <SelectTrigger className="mt-1 rounded-xl h-10" data-testid="assign-time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {times.map((s) => (
                        <SelectItem key={`${s.time}-${s.venue?.workplace_name || ''}`} value={s.time}>
                          {formatTime(s.time)}
                          {s.end_time ? `–${formatTime(s.end_time)}` : ''}
                          {s.duration_minutes ? ` · ${s.duration_minutes} min` : ''}
                          {typeof s.seats_left === 'number' ? ` · ${s.seats_left} seat${s.seats_left === 1 ? '' : 's'} left` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {selected && offline && venue && (
              <p className="text-sm text-[#4B5563] bg-amber-50 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="assign-venue">
                <MapPin className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                <span><span className="text-[#9CA3AF]">Venue: </span>{venueText(venue)}</span>
              </p>
            )}

            <div>
              <Label className="text-sm text-[#6B7280]">Notes for the parent (optional)</Label>
              <Input
                value={notes} onChange={(e) => setNotes(e.target.value)}
                className="mt-1 rounded-xl h-10" placeholder="Anything the family should know"
                data-testid="assign-notes"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="assign-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-xl border-gray-200" onClick={onClose} data-testid="assign-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit} disabled={submitting || loading || !mentorId || !date || !time}
            className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl"
            data-testid="assign-submit-btn"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {rescheduling ? 'Save New Slot' : 'Confirm Assignment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
